import { useState, useEffect } from 'react';
import Loader from '../loader/loader';
import { warnAlert } from '../alerts';
import { fetchSingleUserData } from '../../database/fetch-user-data';
import { updateUser } from '../../database/set-user-data';
import { UserCircleIcon } from '@heroicons/react/24/outline';

function EditUserComponent({ openEditModal, setOpenEditModal, selectedId }) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('User');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [secondaryPhone, setSecondaryPhone] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [isPrimaryEmailValid, setIsPrimaryEmailValid] = useState(true);
  const [isSecondaryEmailValid, setIsSecondaryEmailValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [userImagePreview, setUserImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedId) {
        const user = await fetchSingleUserData(selectedId);
        setName(user.name);
        setUsername(user.username);
        setRole(user.role);
        setPrimaryPhone(user.primaryPhone);
        setSecondaryPhone(user.secondaryPhone);
        setPrimaryEmail(user.primaryEmail);
        setSecondaryEmail(user.secondaryEmail);
        setImageFile(user.imageFile);
        setUserImagePreview(user.photoURL)
      }
    };

    fetchData();
  }, [selectedId]);

  /**
   * The `handlePhoneNumberChange` function formats a phone number input by adding parentheses, spaces,
   * and dashes in the appropriate places.
   * @param event - The event parameter is an object that represents the event that triggered the
   * function. In this case, it is likely an event object related to a change event on an input field.
   */
  const handlePhoneNumberChange = (event, setPhone) => {
    let input = event.target.value.replace(/\D/g, '');

    if (input.length > 10) {
      input = input.substring(0, 10);
    }

    let size = input.length;

    if (size < 4) {
      input = '(' + input;
    } else if (size < 7) {
      input = '(' + input.substring(0, 3) + ') ' + input.substring(3, 6);
    } else {
      input =
        '(' +
        input.substring(0, 3) +
        ') ' +
        input.substring(3, 6) +
        '-' +
        input.substring(6, 10);
    }

    event.target.value = input;
    setPhone(input);
  };

  /**
   * The below code defines two functions, handlePrimaryEmailChange and handleSecondaryEmailChange, which
   * handle changes to the primary and secondary email inputs respectively and validate the email format
   * using a regular expression.
   * @param event - The `event` parameter is an object that represents the event that occurred. In this
   * case, it is an event object related to the email input field. It contains information about the
   * event, such as the target element (the input field), the value of the input field, and other
   * properties and methods
   */
  const handlePrimaryEmailChange = (event) => {
    setPrimaryEmail(event.target.value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsPrimaryEmailValid(emailRegex.test(event.target.value));
  };

  const handleSecondaryEmailChange = (event) => {
    setSecondaryEmail(event.target.value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsSecondaryEmailValid(emailRegex.test(event.target.value));
  };

  /**
   * The handleNameChange function generates a username by replacing spaces with underscores and
   * converting the string to lowercase.
   * @param event - The event parameter is an object that represents the event that triggered the
   * function. In this case, it is an event object that is generated when the value of an input field is
   * changed.
   */
  const handleNameChange = (event) => {
    const generatedUsername = event.target.value
      .replace(/\s+/g, '_')
      .toLowerCase();
    setUsername(generatedUsername);
    setName(event.target.value);
  };
  if (!openEditModal) return null;

  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    const acceptedFileTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxFileSize = 5 * 1024 * 1024; 

    if (file) {
      if (!acceptedFileTypes.includes(file.type)) {
          alert('Please upload a valid image file (JPEG, PNG, GIF,SVG,TIFF,BMP).');
          return;
      }
      if (file.size > maxFileSize) {
          alert('File size exceeds the 5 MB limit.');
          return;
      }
      setImageFile(file);
      setUserImagePreview(URL.createObjectURL(file));
  }
  };
  return (
    <>
      {isLoading && <Loader />}

      <div
        id="editUserData-modal"
        tabIndex="-1"
        aria-hidden="true"
        className={
          openEditModal
            ? 'flex items-center justify-center overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full h-[calc(100%)] max-h-full bg-black bg-opacity-50'
            : 'hidden'
        }
      >
        <div className="relative p-4 w-full max-w-2xl max-h-full">
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
            <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Update User
              </h3>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                onClick={() => setOpenEditModal(false)}
              >
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>
            <div className="p-4 md:p-5 space-y-4">
              <section className="bg-white dark:bg-gray-900">
                <div className="mx-auto max-w-2xl">
                  <form>
                    <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
                    <div className="sm:col-span-2">
                        <label htmlFor="image" className="block mt-2 mx-auto cursor-pointer w-32 h-32  items-center justify-center border rounded-full border-gray-300 relative">
                          <input
                            id="image"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="absolute top-0 left-0 opacity-0 cursor-pointer w-32 h-32"
                          />
                          {userImagePreview ? (
                            <img src={userImagePreview} alt="Image Preview" className="w-full h-full object-cover rounded-full" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-full">
                              <UserCircleIcon className="w-32 h-32" />
                            </div>
                          )}
                        </label>
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="name"
                          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          Full Name <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          value={name}
                          onChange={handleNameChange}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                          placeholder="John Doe"
                          required
                          disabled
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="username"
                          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          Username <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="username"
                          id="username"
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                          placeholder="john_doe"
                          defaultValue={username}
                          required
                          disabled
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="role"
                          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          Role <span className="text-red-600">*</span>
                        </label>
                        <select
                          id="role"
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                          required
                          onChange={(event) => setRole(event.target.value)}
                          value={role}
                        >
                          <option value="User">User</option>
                          <option value="Management">Management</option>
                          <option value="IT_support">IT Support</option>
                          <option value="Admin">Admin</option>
                        </select>
                      </div>
                      <div className="w-full">
                        <label
                          htmlFor="primary_phone"
                          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          Primary Phone <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="primary_phone"
                          id="primary_phone"
                          onChange={(event) =>
                            handlePhoneNumberChange(event, setPrimaryPhone)
                          }
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                          placeholder="(123) 456-7890"
                          value={primaryPhone}
                          required
                        />
                      </div>
                      <div className="w-full">
                        <label
                          htmlFor="secondary_phone"
                          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          Secondary Phone
                        </label>
                        <input
                          type="text"
                          name="secondary_phone"
                          id="secondary_phone"
                          onChange={(event) =>
                            handlePhoneNumberChange(event, setSecondaryPhone)
                          }
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                          placeholder="(123) 456-7890"
                          value={secondaryPhone}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="primary_email"
                          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          Primary Email <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="primary_email"
                          id="primary_email"
                          onChange={handlePrimaryEmailChange}
                          defaultValue={primaryEmail}
                          className={`bg-gray-50 border ${isPrimaryEmailValid
                            ? 'border-gray-300'
                            : 'border-red-600'
                            } text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500`}
                          placeholder="user@domain.com"
                          value={primaryEmail}
                          required
                          disabled
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="secondary_email"
                          className="block text-sm font-medium text-gray-900 dark:text-white"
                        >
                          Secondary Email
                        </label>
                        <input
                          type="text"
                          name="secondary_email"
                          id="secondary_email"
                          onChange={handleSecondaryEmailChange}
                          defaultValue={secondaryEmail}
                          className={`bg-gray-50 border ${isSecondaryEmailValid
                            ? 'border-gray-300'
                            : 'border-red-600'
                            } text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500`}
                          placeholder="user@domain.com"
                          value={secondaryEmail}
                        />
                      </div>
                    </div>
                  </form>
                </div>
              </section>
            </div>
            <div className="flex items-center p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600">
              <button
                onClick={async () => {
                  if (
                    !name ||
                    !username ||
                    !role ||
                    !primaryPhone ||
                    !primaryEmail
                  ) {
                    warnAlert('Please fill in all required fields.');
                    return;
                  }
                  setOpenEditModal(false);
                  setIsLoading(true); // Set loading to true when button is clicked
                  const formData = {
                    name: name || '',
                    username: username || '',
                    role: role || '',
                    primaryPhone: primaryPhone || '',
                    secondaryPhone: secondaryPhone || '',
                    primaryEmail: primaryEmail || '',
                    secondaryEmail: secondaryEmail || '',
                    imageFile:imageFile || ''
                  };
                  await updateUser(selectedId, formData);
                  setIsLoading(false);
                  setName('');
                  setUsername('');
                  setRole('User');
                  setPrimaryPhone('');
                  setSecondaryPhone('');
                  setPrimaryEmail('');
                  setSecondaryEmail('');
                  setIsPrimaryEmailValid(true);
                  setIsSecondaryEmailValid(true);
                  setImageFile(null);
                  setUserImagePreview('')
                }}
                type="button"
                className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
              >
                Update User
              </button>
              <button
                onClick={() => setOpenEditModal(false)}
                type="button"
                className="ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditUserComponent;

/*
! TODO: Add the User to the Database
Then Use the Created Credentials to Create a User on Auth, and send the temporary password to the user
or
Send a new account created email to the user and the new set password link should be created
*/
